import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, IconButton, Button, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, Grid, TableSortLabel
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, YouTube as YouTubeIcon } from '@mui/icons-material';
import api from '../../utils/axios';
import { toast } from 'react-toastify';
import { getComparator, stableSort } from '../../utils/sortUtils';

const ManageSongs = () => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  
  // Sorting State
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('title');

  // New Song State
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [key, setKey] = useState('');
  const [tempo, setTempo] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [chordChart, setChordChart] = useState('');

  useEffect(() => {
    fetchSongs();
  }, []);

  const fetchSongs = async () => {
    try {
      const res = await api.get('/superadmin/songs/recommended');
      if (res.data.success) {
        setSongs(res.data.data);
      }
    } catch (err) {
      toast.error('Failed to load songs');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleAddSong = async () => {
    if (!title || !artist) {
      toast.error('Title and Artist are required');
      return;
    }

    try {
      const payload = { title, artist, key, tempo: Number(tempo) || 0, youtubeUrl, chordChart };
      const res = await api.post('/superadmin/songs/recommended', payload);
      if (res.data.success) {
        toast.success('Song added successfully');
        fetchSongs();
        setOpenDialog(false);
        setTitle('');
        setArtist('');
        setKey('');
        setTempo('');
        setYoutubeUrl('');
        setChordChart('');
      }
    } catch (err) {
      toast.error('Failed to add song');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this recommended song?')) {
      try {
        const res = await api.delete(`/superadmin/songs/recommended/${id}`);
        if (res.data.success) {
          toast.success('Song removed');
          fetchSongs();
        }
      } catch (err) {
        toast.error('Failed to delete song');
      }
    }
  };

  const sortedSongs = stableSort(songs, getComparator(order, orderBy));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Global Staff Recommended Songs</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Song
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" mb={3}>
        These songs will appear in the "Sugestões e Músicas do Momento" section on the mobile app for all churches.
      </Typography>
      
      <Card sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'title'}
                    direction={orderBy === 'title' ? order : 'asc'}
                    onClick={() => handleRequestSort('title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'artist'}
                    direction={orderBy === 'artist' ? order : 'asc'}
                    onClick={() => handleRequestSort('artist')}
                  >
                    Artist
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'key'}
                    direction={orderBy === 'key' ? order : 'asc'}
                    onClick={() => handleRequestSort('key')}
                  >
                    Key / Tempo
                  </TableSortLabel>
                </TableCell>
                <TableCell>Links</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSongs.map((song) => (
                <TableRow key={song._id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{song.title}</TableCell>
                  <TableCell>{song.artist}</TableCell>
                  <TableCell>{song.key || '-'} {song.tempo ? `(${song.tempo} BPM)` : ''}</TableCell>
                  <TableCell>
                    {song.youtubeUrl && (
                      <IconButton href={song.youtubeUrl} target="_blank" color="error">
                        <YouTubeIcon />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleDelete(song._id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {songs.length === 0 && !loading && (
                <TableRow><TableCell colSpan={5} align="center">No recommended songs added yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Recommended Song</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Song Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Artist" value={artist} onChange={(e) => setArtist(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Key (e.g. C, G#)" value={key} onChange={(e) => setKey(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Tempo (BPM)" type="number" value={tempo} onChange={(e) => setTempo(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="YouTube URL" value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Letra com Cifra (Chord Chart)" 
                multiline
                rows={6}
                value={chordChart} 
                onChange={(e) => setChordChart(e.target.value)} 
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
          <Button onClick={handleAddSong} variant="contained" color="primary">Add Song</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageSongs;
